import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Divider,
  DropZoneField,
  Input,
  Radio,
  Select,
  Typography,
} from '@scope/scope-one-ui/core';
import { useNotifications } from 'shared/hooks';
import { useAuth } from 'shared/hooks/useApi';
import { COLORS } from '@scope/scope-one-ui/theme';
import { DownloadTemplateButton, TemplateArea } from './PortfolioUploadSteps.styled';
import { PortfolioUploadSecondStepProps } from './PortfolioUploadSecondStep.d';
import { uploadTypes } from '../constants';
import { downloadService } from '../../../../../services';

export const PortfolioUploadSecondStep: FC<PortfolioUploadSecondStepProps> = ({
  uploadType,
  onFileUpload,
  onPortfolioSelect,
  uploadToISIN,
  portfolio,
  portfoliosOptions,
  isPortfoliosLoading,
}) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { notifyError } = useNotifications();
  const portfolioChoiceScenarios = [
    { value: 'addToExistingPortfolio', label: t('PORTFOLIO_UPLOAD.ADD_TO_EXISTING_PORTFOLIO') },
    { value: 'createNewPortfolio', label: t('PORTFOLIO_UPLOAD.CREATE_NEW_PORTFOLIO') },
  ];

  const [portfolioChoiceScenario, setPortfolioChoiceScenario] = useState(
    portfolioChoiceScenarios[0].value,
  );

  const downloadErrorHandler = () => {
    notifyError(t('PORTFOLIO_UPLOAD.FAILED_DOWNLOAD_PORTFOLIO_TEMPLATE'));
  };

  const downloadFile = (type = '') =>
    downloadService({
      path: `/user-profile/portfolios/upload/${uploadType}${type}`,
      name: `portfolio-template${type.replace('/', '-')}.xlsx`,
      onError: downloadErrorHandler,
    });

  const handleDownloadTemplateClick = () => {
    if (uploadType === uploadTypes.funds) {
      downloadFile();
      return;
    }

    if (auth.roles.includes('PERSONAL_ADVISOR')) {
      downloadFile('/personal-advisor');
    }

    downloadFile('/common');
  };

  const fileAddedHandler = (files) => {
    if (files && files[0]) {
      onFileUpload(files[0].file);
    }
  };

  return (
    <>
      <Box pt={4} px={3} display="flex" flexDirection="column">
        <Typography color={COLORS.typography.main}>
          {uploadType === uploadTypes.entities
            ? t('PORTFOLIO_UPLOAD.INFO_ENTITIES_2_1')
            : t('PORTFOLIO_UPLOAD.INFO_FUNDS_2_1')}
        </Typography>
        <Box mt={3}>
          <Radio
            options={portfolioChoiceScenarios}
            value={portfolioChoiceScenario}
            onChange={(value) => {
              setPortfolioChoiceScenario(value as string);
              return value === portfolioChoiceScenarios[1].value && onPortfolioSelect(null);
            }}
          />
        </Box>
        <Box mt={3} mb={4}>
          {portfolioChoiceScenario === portfolioChoiceScenarios[0].value ? (
            <Select
              value={(portfolio && portfolio.portfolioId) ?? undefined}
              options={portfoliosOptions}
              disabled={isPortfoliosLoading}
              placeholder={isPortfoliosLoading ? 'Loading Portfolios...' : 'Select portfolio'}
              onChange={(val) => onPortfolioSelect({ portfolioId: val as string })}
            />
          ) : (
            <Input
              placeholder={t('PORTFOLIO_UPLOAD.TYPE_PORTFOLIO_NAME')}
              onChange={(val) => onPortfolioSelect({ portfolioName: val as string })}
            />
          )}
        </Box>
      </Box>
      <Divider />
      <Box pt={4} pb={5} px={3} display="flex" flexDirection="column">
        <TemplateArea mb={4}>
          <Typography>
            <b>{t('PORTFOLIO_UPLOAD.DOWNLOAD_INFO_1')}</b>
            {t('PORTFOLIO_UPLOAD.DOWNLOAD_INFO_2')}
          </Typography>
          <DownloadTemplateButton onClick={() => handleDownloadTemplateClick()}>
            {t('PORTFOLIO_UPLOAD.DOWNLOAD_TEMPLATE')}
          </DownloadTemplateButton>
        </TemplateArea>
        <DropZoneField
          containerSize="medium"
          fileType="sheet"
          handleOnUpload={fileAddedHandler}
          multiple={false}
        />
        {uploadType === uploadTypes.entities && (
          <Box mt={4}>
            <Checkbox
              onChange={(val) => uploadToISIN(val)}
              label={t('PORTFOLIO_UPLOAD.ADD_TO_ISIN')}
            />
          </Box>
        )}
      </Box>
      <Divider />
    </>
  );
};
